/**
 * Call system contracts — shared by the dashboard server (broker routes)
 * and the web client (useCall hook, in-call window). Single source of truth
 * so the two sides can never disagree about state machine values, end-reason
 * codes, or room-coordinate shapes.
 *
 * Phase 1 foundation — see docs/calls.md for the full three-plane
 * architecture (signaling / control broker / JsSIP media).
 */

/** All valid states in a call's lifecycle. */
export const CALL_STATES = ["ringing", "connecting", "connected", "ended"] as const;
export type CallState = (typeof CALL_STATES)[number];

/** Why a call ended (carried on the `call_state` frame when state = "ended"). */
export const CALL_END_REASONS = [
  "declined",
  "missed",
  "cancelled",
  "busy",
  "completed",
  "failed",
] as const;
export type CallEndReason = (typeof CALL_END_REASONS)[number];

/**
 * The blurb shown on the brief "ended" card, derived from the end reason.
 *
 * Shared so BOTH sides of a call word a failure identically: when a call can't
 * be set up (the conferweb broker refused a room), the answerer learns from the
 * 502 on its own `accept` and the caller learns from the `ended`/`failed`
 * realtime frame — they must not disagree about what happened. Before this,
 * every ended state rendered a flat "Call ended", so a genuine infrastructure
 * failure was indistinguishable from a normal hang-up (the "we just accept and
 * nothing happens" report).
 */
export function callEndedLabel(reason?: CallEndReason): string {
  switch (reason) {
    case "failed":
      return "Call failed — couldn't connect. Please try again.";
    case "busy":
      return "Line busy";
    case "declined":
      return "Call declined";
    case "missed":
      return "No answer";
    case "cancelled":
      return "Call cancelled";
    default:
      return "Call ended";
  }
}

/** The media-layer coordinates handed to the callee once both parties have
 * accepted and the broker has allocated a conference room. */
export interface CallRoomCoords {
  roomId: number;
  participantPin: string;
  mediaWsUrl: string;
  sipDomain: string;
  /** The room's MODERATOR pin (from the C1 room-create response). Forwarded only
   * for the consultation-style outbound dial (a direct JsSIP INVITE to a PSTN
   * number carries `X-XOP: <conferenceId>/<moderatorPin>` — the H&H consultation
   * routing context). Absent on rooms minted before this was threaded. */
  moderatorPin?: string;
}

/** Payload embedded in a `call_invite` server→client frame. The callee
 * receives this the moment an inbound call arrives so it can render the
 * incoming-call notification without a round-trip. */
export interface CallInvitePayload {
  call_id: string;
  from_user_id: number;
  from_user_name: string;
  created_at: string;
}

/** Payload for a `call_state` server→client frame. Carries the minimum
 * fields needed to drive the client's state machine; `reason` and `room`
 * are conditionally spread (exactOptionalPropertyTypes). */
export interface CallStatePayload {
  call_id: string;
  state: CallState;
  reason?: CallEndReason;
  room?: CallRoomCoords;
}

/** Payload for a `call_screen` server→client frame — signals that one party
 * started or stopped sharing their screen. The receiver uses `sharing` to
 * show/hide the remote-share view; `sharer_session_id` is the conferweb
 * sessionId of the sharer so the media client can attach to the right stream.
 * Phase 3 Track A. */
export interface CallScreenPayload {
  call_id: string;
  sharing: boolean;
  sharer_session_id: number;
}

/** Payload for a `call_camera` server→client frame — signals that one party
 * turned their camera on or off. Mirrors `call_screen`: the receiver uses
 * `sharing` to subscribe/unsubscribe and `sharer_session_id` (the publisher's
 * conferweb sessionId) to attach to the right camera stream. Audio-first calls
 * publish the camera as a separate SFU session, like screen-share. */
export interface CallCameraPayload {
  call_id: string;
  sharing: boolean;
  sharer_session_id: number;
}

/** One entry in a group call's participant roster. */
export interface CallRosterParticipant {
  user_id: number;
  name: string;
  state: "ringing" | "joined";
  /** The participant's conferweb SFU session id (the `accountData.sid` from their
   * `/auth`), once they've reported it. Every client uses it to open a per-peer
   * receive-only camera view leg (`v-<sid>/r`) — the all-to-all group video mesh.
   * Absent until the participant has joined media + reported it. */
  conferweb_sid?: number;
}

/** Payload for a `call_roster` server→client frame — the current participant
 * list for a (group) call. Broadcast to every participant whenever someone is
 * invited, joins, or leaves, so each client can render the roster and know who
 * to subscribe to. A 1:1 call simply has two entries. Phase 11 (group calls). */
export interface CallRosterPayload {
  call_id: string;
  participants: CallRosterParticipant[];
}
